import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import reactFadeIn from "react-fade-in";
import { IconPlus } from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import FeedIcon from "../../assets/icons/feed.svg?react";
import { RoomHeader } from "../../components/room/RoomHeader";
import { Tabs } from "../../components/ui/Tabs";
import TabSwitcher from "../../components/app/TabSwitcher";
import InfographicCard from "../../components/cards/InfographicCard";
import PlaylistCard from "../../components/cards/PlaylistCard";
import VideoCard from "../../components/cards/VideoCard";
import { Select } from "../../components/ui/Select";
import SkeletonContentCard from "../../components/profile/SkeletonContentCard";
import {
  RoomBlockHeading,
  RoomListWrapper,
  RoomPageWrapper,
  RoomResourcesTabsContainer,
} from "../../components/room/layout";
import RoomEventDisplay from "../../components/room/RoomEventDisplay";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomColors } from "../../domains/room/utils/getRoomColors";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useRoomInfoHeader } from "../../domains/room/hooks/useRoomInfoHeader";
import { useJoinRoom } from "../../domains/room/hooks/useJoinRoom";
import { useCanShowJoin } from "../../domains/room/hooks/useCanShowJoin";
import { useShareRoom } from "../../domains/room/hooks/useShareRoom";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { useGetRecommendedPlaylistsQuery } from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import {
  ALL_CONFERENCES_ID,
  useGetCongressContentQuery,
  type CongressContentItem,
} from "../../domains/room/hooks/useGetCongressContentQuery";
import { useApiQuery } from "../../services/api";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { EventDisplayDestination } from "../../../../api/src/shared/domain/room";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListMixedContent } from "../../tools/analytics/domains/content.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { type Api } from "../../services/api";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import type { ApiPlaylistModel } from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import RoomExplainerBottomSheet from "../../components/room/RoomExplainerBottomSheet";
import { Button } from "../../components/ui/Button";
import Loader from "../../components/Loader";

const FadeIn = cjsDefault(reactFadeIn);

type TaggedContent = {
  kind: "content";
  data: CongressContentItem;
};
type TaggedPlaylist = { kind: "playlist"; data: ApiPlaylistModel };
type CombinedItem = TaggedContent | TaggedPlaylist;
type ApiCongressModel =
  Api["get"]["/webapp/private/congresses"]["output"]["body"][number];

const debug = new DebugLogger("RoomConferencesPage");
debug.mute();
const ANALYTICS_LIST_NAME = AnalyticsItemList.ROOM_CONGRESSES;

const getTime = (item: CombinedItem) => {
  const dateStr =
    item.kind === "content"
      ? item.data.publication_date
      : item.data.publicationDate;
  return new Date(dateStr ?? item.data.meta.creationDate).getTime();
};

export default function RoomConferencesPage({
  roomId,
  room,
}: {
  roomId: string;
  room: PrivateRoom;
}) {
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(roomId);
  const location = useLocation();
  const currentRoom = room;
  const { roomBackgroundColor, roomTextColor } = getRoomColors(
    room.primaryColor
  );
  // `activeLang` is the congress language filter below, which matches
  // congress.language against the interface locale. Room text belongs on
  // `roomTextLang`.
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { triggerShare, renderShareSheet } = useShareRoom(roomId, room);
  const isCongressTabEnabled = currentRoom.congressTab === true;
  const isResourcesTabEnabled = currentRoom.resourcesTab === true;
  const urlParams = new URLSearchParams(location.search);
  const selectedConferenceId = urlParams.get("congress") ?? ALL_CONFERENCES_ID;
  const congressQuery = useApiQuery("/webapp/private/congresses", {
    query: { roomId },
  });
  const congressList =
    congressQuery.state === "success" ? congressQuery.body : [];
  // NOTE: This is a temporary solution to group congresses
  //  with the same name across all languages
  const conferenceIdGroups = congressList.reduce<Record<string, string[]>>(
    (groups: Record<string, string[]>, congress: ApiCongressModel) => {
      const { id, name } = congress;
      const group = groups[name] ?? [];
      group.push(id);
      groups[name] = group;
      return groups;
    },
    {}
  );
  const currentConferenceIdGroup: string[] =
    Object.values(conferenceIdGroups).find((group) => {
      return group.includes(selectedConferenceId);
    }) ?? [];
  const filteredCongressOptions = congressList.filter(
    (congress: ApiCongressModel) => congress.language === activeLang
  );
  const displayedOptionIds = new Set([
    ALL_CONFERENCES_ID,
    ...filteredCongressOptions.map((c: ApiCongressModel) => c.id),
  ]);
  const contentCache = useGetCongressContentQuery(roomId, {
    congress: selectedConferenceId,
  });
  const {
    content: displayedContent,
    contentIds: displayedContentIds,
    isLoading: isLoadingContent,
  } = contentCache;
  const playlistsCache = useGetRecommendedPlaylistsQuery(roomId);
  const {
    playlists,
    playlistIds,
    isLoading: isLoadingPlaylists,
  } = playlistsCache;
  const filteredPlaylists = filterPlaylistsForCongress(
    playlists,
    selectedConferenceId
  );
  const combinedContent: CombinedItem[] = [
    ...displayedContent.map((data): CombinedItem => ({
      kind: "content",
      data,
    })),
    ...filteredPlaylists.map((data): CombinedItem => ({
      kind: "playlist",
      data,
    })),
  ].sort((a, b) => {
    return getTime(b) - getTime(a); // Newest first
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);
  const canShowJoin = useCanShowJoin(roomId, room);
  const [isExplainerOpen, setExplainerOpen] = useState(false);
  const { joinRoom, isJoining } = useJoinRoom(roomId, "public app", "greet");
  const joinButton = canShowJoin ? (
    <Button
      variant="primary"
      className="w-full"
      disabled={isJoining}
      leftIcon={isJoining ? null : <IconPlus className="size-5!" stroke={2} />}
      onClick={joinRoom}
    >
      {isJoining ? <Loader /> : t("Join")}
    </Button>
  ) : null;
  const {
    handleScroll: handleRoomInfoScroll,
    hasScrolledPastInfo,
    RoomInfoSection,
  } = useRoomInfoHeader(roomId, {
    footer: joinButton,
    onInfoClick: () => {
      setExplainerOpen(true);
    },
  });

  // Scroll to top on forward navigation (tab switch), restore on back
  useLayoutEffect(() => {
    if (history.action !== "POP") {
      scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  useEffectOnce(() => {
    if (
      !isLoadingContent &&
      !isLoadingPlaylists &&
      combinedContent.length > 0
    ) {
      restorePageScrollY();
    }
  }, [isLoadingContent, isLoadingPlaylists]);

  useEffectOnce(() => {
    if (displayedContent.length > 0 || playlists.length > 0) {
      gaEventViewItemListMixedContent({
        content: displayedContent,
        listName: ANALYTICS_LIST_NAME,
      });
    }
  }, [displayedContentIds, playlistIds]);

  useEffect(() => {
    if (!displayedOptionIds.has(selectedConferenceId)) {
      urlParams.set("congress", ALL_CONFERENCES_ID);
      history.replace({
        pathname: location.pathname,
        search: urlParams.toString(),
      });
    }
  }, [activeLang]);

  function handleSelectCongress(option: { value: string }) {
    urlParams.set("congress", option.value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  }

  const conferenceOptions = [
    { label: t("All conferences"), value: ALL_CONFERENCES_ID },
    ...filteredCongressOptions.map((congress: ApiCongressModel) => ({
      label: congress.name,
      value: congress.id,
    })),
  ];

  // Filter playlists based on selected congress
  function filterPlaylistsForCongress(
    playlists: ApiPlaylistModel[],
    _selectedCongressId: string
  ) {
    return playlists;
  }

  function handleScrollEnd() {
    if (contentCache.canFetchNextPage) contentCache.fetchNextPage();
    if (playlistsCache.canFetchNextPage) playlistsCache.fetchNextPage();
  }

  return (
    <>
      <ScrollableLayout
        scrollRef={scrollRef}
        headerClassName="bg-white/90 backdrop-blur-md"
        onScroll={() => {
          handleRoomInfoScroll();
          savePageScrollY();
        }}
        header={
          <RoomHeader
            roomId={roomId}
            backTo="/rooms"
            desktopBackTo={null}
            backLabel={null}
            showJoinButton={hasScrolledPastInfo && canShowJoin}
            currentUser={currentUser}
            onJoinClick={joinRoom}
            onShareClick={() => {
              void triggerShare();
            }}
          />
        }
        footer={null}
        style={{ paddingTop: 0, overscrollBehavior: "none" }}
        onScrollEnd={handleScrollEnd}
      >
        {RoomInfoSection}

        <Tabs
          className="sticky top-[calc(48px+var(--safe-area-top))] z-10"
          tabs={[
            {
              value: "discovery",
              label: t("Discovery"),
              badge: null,
              icon: null,
            },
            {
              value: "resources",
              label: t("Resources"),
              badge: null,
              icon: null,
              activeStyle: { color: currentRoom.primaryColor ?? undefined },
              inactiveStyle: {
                color: currentRoom.primaryColor ?? undefined,
                backgroundColor:
                  currentRoom.primaryColor !== null
                    ? `${currentRoom.primaryColor}0F`
                    : undefined,
              },
              indicatorStyle:
                currentRoom.primaryColor !== null
                  ? { backgroundColor: currentRoom.primaryColor }
                  : undefined,
            },
          ]}
          activeTab="resources"
          onTabChange={(tab) => {
            if (tab === "discovery") {
              history.push(`/room/discovery/${roomId}`);
            }
          }}
        />

        <RoomPageWrapper style={{ backgroundColor: roomBackgroundColor }}>
          <div
            className="p-[16px_8px]  flex flex-col gap-[16px]"
            style={{ backgroundColor: roomBackgroundColor }}
          >
            <RoomBlockHeading
              style={{
                margin: "0 16px",
                padding: 0,
                color: roomTextColor,
                fill: roomTextColor,
              }}
            >
              <FeedIcon />
              {currentRoom.organisationModel.label !== null ? (
                <Trans
                  i18nKey="{{label}} Resources"
                  values={{ label: currentRoom.organisationModel.label }}
                />
              ) : (
                t("Resources")
              )}
            </RoomBlockHeading>

            {isCongressTabEnabled && isResourcesTabEnabled && (
              <RoomResourcesTabsContainer>
                <TabSwitcher
                  tabs={[
                    { label: t("Conferences") },
                    { label: t("Resources") },
                  ]}
                  activeTab={0}
                  onTabChange={(index: number) => {
                    if (index === 1) {
                      history.push(`/room/resources/${roomId}`);
                    }
                  }}
                  noSlider
                />
              </RoomResourcesTabsContainer>
            )}

            <div className="mx-[16px]">
              <Select
                appearance="field"
                footerAction={null}
                searchOnlyOptions={null}
                value={selectedConferenceId}
                options={conferenceOptions}
                onChange={handleSelectCongress}
                placeholder={t("Conferences")}
                className="bg-white"
              />
            </div>
          </div>
          {isLoadingContent || isLoadingPlaylists ? (
            <FadeIn key="loading">
              <RoomListWrapper>
                {Array.from({ length: 10 }).map((_, index) => (
                  <SkeletonContentCard key={index} />
                ))}
              </RoomListWrapper>
            </FadeIn>
          ) : (
            <FadeIn key="content">
              <RoomEventDisplay
                roomId={roomId}
                roomTextColor={roomTextColor ?? null}
                pageType={EventDisplayDestination.CONGRESS}
                conferenceIds={currentConferenceIdGroup}
              />
              <RoomListWrapper>
                {combinedContent.length === 0 && (
                  <span className="no-results">
                    {t("No content available yet for this conference")}
                  </span>
                )}
                {combinedContent.map((item) => {
                  if (item.kind === "content") {
                    if (item.data.content_format === "Video") {
                      return (
                        <VideoCard
                          roomId={roomId}
                          key={item.data.id}
                          video={item.data}
                          analyticsListName={ANALYTICS_LIST_NAME}
                          recommendation={null}
                          onClick={savePageScrollY}
                          className={null}
                        />
                      );
                    }
                    if (item.data.content_format === "Infographic") {
                      return (
                        <InfographicCard
                          roomId={roomId}
                          key={item.data.id}
                          infographic={item.data}
                          analyticsListName={ANALYTICS_LIST_NAME}
                          recommendation={null}
                          onClick={savePageScrollY}
                          className={null}
                        />
                      );
                    }
                    return null;
                  }
                  return (
                    <PlaylistCard
                      key={item.data.id}
                      playlist={item.data}
                      variant="in-room"
                      primaryColor={room.primaryColor}
                      secondaryColor={room.secondaryColor}
                      onClick={() => {
                        savePageScrollY();
                        gaEventSelectItemPlaylist({
                          playlist: item.data,
                          listName: ANALYTICS_LIST_NAME,
                        });
                        history.push({
                          pathname: publicPlaylistLink(item.data),
                          state: { playlist: item.data },
                        });
                      }}
                    />
                  );
                })}
              </RoomListWrapper>
            </FadeIn>
          )}
        </RoomPageWrapper>
      </ScrollableLayout>

      {renderShareSheet()}

      <RoomExplainerBottomSheet
        roomId={roomId}
        presentation="sheet"
        isOpen={isExplainerOpen}
        onClose={() => {
          setExplainerOpen(false);
        }}
      />
    </>
  );
}
